<template>
  <div class="portlet light portlet-fit timesheet-wrapper" style="min-width: 1400px; overflow-x: auto">
    <div class="portlet-title d-flex">

      <!-- Contenu titre à gauche -->
      <div class="caption mr-auto">
        <span>
          <i class="fa fa-clock-o"></i> Feuille de temps
        </span>
        <template v-if="timesheet != null">
          <span v-if="timesheet.timesheet_status == '0'" class="badge bg-green-jungle bold"><i class="fa fa-edit"></i> Brouillon</span>
          <span v-else-if="timesheet.timesheet_status == '1'" class="badge bg-yellow-gold bold"><i class="fa fa-clock-o"></i> En approbation</span>
          <span v-else-if="timesheet.timesheet_status == '2'" class="badge bg-red bold"><i class="fa fa-check"></i> Approuvée</span>
        </template><br>
        <select class="form-control mt-1" style="width: auto" @change="loadTimesheet" v-model="sortOrder">
          <option value="">Ordre de trie...</option>
          <option value="project_number">Numéro de projet</option>
        </select>
      </div>

      <!-- Centre -->
      <div v-if="timesheet != null">
        <div class="text-center" style="font-size: 22px">{{timesheet.timesheet_employee_name}}</div>
        <div class="mt-2">
          <week-picker :value="startDate" @change="handleChangeDate" />
        </div>
      </div>
      
      <!-- Boutons de droite -->
      <div class="ml-auto d-flex flex-wrap">
        <div v-if="timesheet != null">
          <button v-if="timesheet.timesheet_status == '0'" class="btn blue mr-1" @click="sendTimesheetAsk">
            <i class="fa fa-send"></i> Envoyer pour approbation
          </button>
          <template v-else-if="timesheet.timesheet_status == '1' && canApprove">
            <button class="btn green-jungle mr-1" @click="approveTimesheetAsk">
              <i class="fa fa-check"></i> Approuver
            </button>
            <button class="btn red mr-1" @click="returnTimesheetAsk">
              <i class="fa fa-times"></i> Retourner
            </button>
          </template>
        </div>
      </div>

    </div>

    <div class="portlet-body" v-if="loading"> Loading. </div>
    <div class="portlet-body" v-else>
      
      <timesheet-section 
        :rows="timeRows"
        :title="'Heures projets'"
        :activityNameLabel="'Activité'"
        :activityNumberLabel="'# Act'"
        :activityType="'activity'"
        :start="startDate"
        :end="endDate"
        @rowUpdated="rowUpdated"
        @rowDeleted="deleteRow"
        :editMode="editMode"
        :rowType="'1'"
        :valueDecimals="2"
      />
      
      <timesheet-section 
        :rows="expenseRows"
        :title="'Dépenses'"
        :activityNameLabel="'Dépense'"
        :activityNumberLabel="'Type'"
        :activityType="'expense'"
        :start="startDate"
        :end="endDate"
        @rowUpdated="rowUpdated"
        @rowDeleted="deleteRow"
        :editMode="editMode"
        :rowType="'2'"
        :valueDecimals="2"
        :commentText="'Incluant TPS et TVQ'"
      />
      
      <timesheet-section 
        :rows="milageRows"
        :title="'Kilométrage'"
        :activityNameLabel="''"
        :activityNumberLabel="''"
        :start="startDate"
        :end="endDate"
        @rowUpdated="rowUpdated"
        @rowDeleted="deleteRow"
        :editMode="editMode"
        :rowType="'3'"
        :valueDecimals="2"
        :displayActivityInputs="false"
      />

      <div v-if="timesheet != null">
        <div>
          <div class="row">

            <div class="col-md-6">
              <timesheet-expense-totals
                :rows="rows"
                :tps="timesheet.timesheet_tps_value"
                :tvq="timesheet.timesheet_tvq_value"
                :kmRate="timesheet.timesheet_km_rate"
              />
            </div>

            <div class="col-md-6">
              <timesheet-paid-hour-totals 
                :rows="rows" 
                :timesheet="timesheet"
                :allocation="{
                  sicknessHours: timesheet.timesheet_start_sickness_hours,
                  holidayHours: timesheet.timesheet_start_holiday_hours,
                  vacationHours: timesheet.timesheet_start_vacation_hours,
                  bankHours: timesheet.timesheet_start_bank_hours,
                }"
                :editMode="editMode"
                :canAdjust="canApprove && timesheet.timesheet_status == '1'"
                @adjustmentChanged="adjustmentChanged"
              />
            </div>

          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
const { DateTime } = require("luxon");
import WeekPicker from '../WeekPicker.vue';
import TimesheetExpenseTotals from './TimesheetExpenseTotals.vue';
import TimesheetPaidHourTotals from './TimesheetPaidHourTotals.vue';
import TimesheetSection from './TimesheetSection.vue';

export default {
  components: { TimesheetSection, TimesheetExpenseTotals, TimesheetPaidHourTotals, WeekPicker },
  name: "timesheet",
  props: {
    userId: {
      type: Number,
      required: true
    },
    timesheetId: {
      type: Number,
      required: true
    },
    timesheetContactId: {
      type: Number,
      required: true
    },
    start: {
      type: String,
      required: true
    },
    end: {
      type: String,
      required: true
    },
    canApprove: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: true,
      editMode: true,
      rows: [],
      timesheet: null,
      updateCounter: 0,
      startDate: this.start,
      endDate: this.end,
      sortOrder: ""
    }
  },
  created() {
    this.loadTimesheet();
  },
  computed: {
    timeRows() {
      this.updateCounter;
      let rows = [...this.rows.filter(r => r.timesheet_entry_type == "1")];

      if(this.editMode){
        rows.push({
          project_number: null,
          project_name: null,
          project_phase_number: null,
          project_phase_name: null,
          total: "",
          comment: "",
          partial: true,
          days: {}
        });
      }

      return rows;
    },
    expenseRows() {
      this.updateCounter;
      let rows = [...this.rows.filter(r => r.timesheet_entry_type == "2")];

      if(this.editMode){
        rows.push({
          project_number: null,
          project_name: null,
          project_phase_number: null,
          project_phase_name: null,
          total: "",
          comment: "",
          partial: true,
          days: {}
        });
      }

      return rows;
    },
    milageRows() {
      this.updateCounter;
      let rows = [...this.rows.filter(r => r.timesheet_entry_type == "3")];

      if(this.editMode){
        rows.push({
          project_number: null,
          project_name: null,
          project_phase_number: null,
          project_phase_name: null,
          total: "",
          comment: "",
          partial: true,
          days: {}
        });
      }

      return rows;
    },
    maxRowNumber() {
      let max = 0;
      for (const row of this.rows) {
        max = Math.max(row.timesheet_entry_row, max);
      }
      return max;
    }
  },
  methods: {
    handleChangeDate(date) {
      this.startDate = date;
      this.endDate = DateTime.fromSQL(date).plus({days: 6}).toSQLDate();
      this.loadTimesheet();
    },
    async loadTimesheet() {
      try {
        let data = await $.get(`${window.baseUrl}timesheet/get/${this.startDate}/${this.timesheetContactId}?sort=${this.sortOrder}`);

        this.timesheet = data.timesheet;

        this.timesheet.timesheet_start_sickness_hours = parseFloat(this.timesheet.timesheet_start_sickness_hours)
        this.timesheet.timesheet_start_holiday_hours = parseFloat(this.timesheet.timesheet_start_holiday_hours)
        this.timesheet.timesheet_start_vacation_hours = parseFloat(this.timesheet.timesheet_start_vacation_hours)
        this.timesheet.timesheet_start_bank_hours = parseFloat(this.timesheet.timesheet_start_bank_hours)

        this.timesheet.timesheet_remaining_sickness_hours = parseFloat(this.timesheet.timesheet_remaining_sickness_hours)
        this.timesheet.timesheet_remaining_holiday_hours = parseFloat(this.timesheet.timesheet_remaining_holiday_hours)
        this.timesheet.timesheet_remaining_vacation_hours = parseFloat(this.timesheet.timesheet_remaining_vacation_hours)
        this.timesheet.timesheet_remaining_bank_hours = parseFloat(this.timesheet.timesheet_remaining_bank_hours)

        this.timesheet.timesheet_adjustment_sickness_hours = parseFloat(this.timesheet.timesheet_adjustment_sickness_hours)
        this.timesheet.timesheet_adjustment_holiday_hours = parseFloat(this.timesheet.timesheet_adjustment_holiday_hours)
        this.timesheet.timesheet_adjustment_vacation_hours = parseFloat(this.timesheet.timesheet_adjustment_vacation_hours)
        this.timesheet.timesheet_adjustment_bank_hours = parseFloat(this.timesheet.timesheet_adjustment_bank_hours)

        this.rows.splice(0, this.rows.length, ...data.rows);
        this.loading = false;
        this.editMode = this.timesheet.timesheet_status == "0";
      } catch(e){
        console.error(e);
      }
    },
    rowUpdated(row, data, type) {
      let r = this.rows.find(e => e == row);
      let isnew = false;

      if(r === undefined) {
        r = {
          project_id: null,
          project_number: null,
          project_name: null,
          project_phase_id: null,
          project_phase_number: null,
          project_phase_name: null,
          timesheet_entry_comment: "",
          timesheet_entry_type: type,
          timesheet_entry_id: 0,
          timesheet_entry_name: "",
          timesheet_entry_number: "",
          timesheet_entry_row: this.maxRowNumber+1,
          days: {}
        };

        this.rows.push(r)
      }

      if(data.day === undefined){
        for (const field in data) {
          const value = data[field];
          r[field] = value;
        }
      } else {
        let date = data.day.date;
        let value = data.day.value;

        if(value == "" && r.days[date] !== undefined){
          delete r.days[date]
        } else {
          if(r.days[date] === undefined) {
            this.$set(r.days, date, {
              timesheet_entry_id: 0,
              timesheet_entry_value: value
            });
          } else {
            r.days[date].timesheet_entry_value = value;
          }
        }
      }

      if(this.timesheet.timesheet_id == 0) {
        this.saveTimesheet().then(() => {
          this.saveRow(r);
        })
      } else {
        this.saveRow(r);
      }

      this.updateCounter++;
    },
    async saveTimesheet() {
      if(this.timesheet.timesheet_id == 0) {

        try {
          let response = await $.post(`${window.baseUrl}timesheet/add/save`, {form: this.timesheet, accept: "json"});
          this.timesheet.timesheet_id = response.inserted_id;
        } catch(err) {
          toastr.error(`Une erreur s'est produite lors de la sauvegarde de la feuille de temps.`);
          console.error(err);
        }

      } else {

        try {
          await $.post(`${window.baseUrl}timesheet/edit/save`, {form: this.timesheet, id: this.timesheet.timesheet_id, accept: "json"});
        } catch(err) {
          toastr.error(`Une erreur s'est produite lors de la sauvegarde de la feuille de temps.`);
          console.error(err);
        }

      }
    },
    async saveRow(row) {
      row.timesheet_entry_timesheet_id = this.timesheet.timesheet_id;

      try {
        let response = await $.post(`${window.baseUrl}timesheet/view/${this.timesheet.timesheet_id}/saverow`, row);
        row.timesheet_entry_id = response.timesheet_entry_id;
        if ($(".has-custom-error").length > 0) {
	        validateEntries(row);
        }
      } catch(err) {
        toastr.error(`Une erreur s'est produite lors de la modification`);
        console.error(err);
      }
    },
    async deleteRow(row) {
      let ix = this.rows.findIndex(e => e == row);

      this.rows.splice(ix,1);
      this.updateCounter++;

      try {
        let response = await $.post(`${window.baseUrl}timesheet/view/${this.timesheet.timesheet_id}/deleterow`, {row:row.timesheet_entry_row});
      } catch(err) {
        toastr.error(`Une erreur s'est produite lors de la supression de la ligne.`);
        console.error(err);
      }
    },
    sendTimesheetAsk() {
      let that = this;

      bootbox.dialog({
        title: "Envoyer la feuille de temps",
        message: `Voulez-vous vraiment envoyer cette feuille de temps pour approbation?`,
        buttons: {
          confirm: {
            label: "Confirmer et envoyer",
            className: "blue",
            callback: async function() {
              if (validateEntries()) {
                return await that.sendTimesheet();
              } else {
                bootbox.alert("Veuillez d'abord corriger les entrées incomplètes.");
              }
            }
          },
          cancel: {
            label: "Annuler",
            className: "secondary",
            callback: function() {},
          }
        }
      })
    },
    async sendTimesheet() {
      try {
        let response = await $.post(`${window.baseUrl}timesheet/view/${this.timesheet.timesheet_id}/send`);
        this.timesheet.timesheet_status = "1";
        this.editMode = false;
      } catch(err) {
        toastr.error(`Une erreur s'est produite lors de l'envoi de la feuille de temps.`);
        console.error(err);
      }
    },
    approveTimesheetAsk() {
      let that = this;

      bootbox.dialog({
        title: "Approuver la feuille de temps",
        message: `Voulez-vous vraiment approuver cette feuille de temps?`,
        buttons: {
          confirm: {
            label: "Approuver",
            className: "green-jungle",
            callback: async function() {
              return await that.approveTimesheet();
            }
          },
          cancel: {
            label: "Annuler",
            className: "secondary",
            callback: function() {},
          }
        }
      })
    },
    returnTimesheetAsk() {
      let that = this;

      bootbox.dialog({
        title: "Approuver/Retourner la feuille de temps",
        message: `Voulez-vous vraimer retourner cette feuille de temps à l'employé(e) pour corrections?`,
        buttons: {
          confirm: {
            label: "Retourner à l'employé(e)",
            className: "red",
            callback: async function() {
              return await that.returnTimesheet();
            }
          },
          cancel: {
            label: "Annuler",
            className: "secondary",
            callback: function() {},
          }
        }
      })
    },
    async approveTimesheet() {
      try {
        let response = await $.post(`${window.baseUrl}timesheet/view/${this.timesheet.timesheet_id}/approve`);
        this.timesheet.timesheet_status = "2";
        this.editMode = false;
      } catch(err) {
        toastr.error(`Une erreur s'est produite lors de l'approbation de la feuille de temps.`);
        console.error(err);
      }
    },
    async returnTimesheet() {
      try {
        let response = await $.post(`${window.baseUrl}timesheet/view/${this.timesheet.timesheet_id}/return`);
        this.timesheet.timesheet_status = "0";
        this.editMode = true;
      } catch(err) {
        toastr.error(`Une erreur s'est produite lors du retour de la feuille de temps.`);
        console.error(err);
      }
    },
    adjustmentChanged() {
      this.saveTimesheet();
    }
  },
}

function validateEntries(row) {
  //console.log(row);
  // On cache les erreurs
  $(".has-custom-error").removeClass("has-custom-error");
  $(".custom-error").hide();
  // On valide les entrées
  var has_error = false;
  $(".timesheet-entry-row").each(function() {
    var tr = $(this);
    // S'il y a au moins un champs avec une valeur, on vérifie que les champs obligatoires sont remplis pour envoyer pour approbation
    var has_value = false;
    $(this).find("select, input").each(function() {
      if ($(this).val() != "" && $(this).val() != null) {
        has_value = true;
      }
    });

    if (has_value) {

      // On vérifie si on a un projet de sélectionné
      if (tr.find(".timesheet-entry-project").find("select").val() == 0 || tr.find(".timesheet-entry-project").find("select").val() == null) {
        tr.find(".timesheet-entry-project").find(".select2-selection").addClass("has-custom-error");
        tr.next().find(".timesheet-entry-project").closest("td").find(".custom-error").show();
        has_error = true;
      }

      // On vérifie si on a une phase de sélectionné
      if (tr.find(".timesheet-entry-phase").find("select").val() == 0 || tr.find(".timesheet-entry-phase").find("select").val() == null) {
        tr.find(".timesheet-entry-phase").find(".select2-selection").addClass("has-custom-error");
        tr.next().find(".timesheet-entry-phase").closest("td").find(".custom-error").show();
        has_error = true;
      }

      // On vérifie si on a une activité de sélectionné
      if (tr.find(".timesheet-entry-activity").length != 0) {
        if (tr.find(".timesheet-entry-activity").find("select").val() == 0 || tr.find(".timesheet-entry-activity").find("select").val() == null) {
          tr.find(".timesheet-entry-activity").find(".select2-selection").addClass("has-custom-error");
          tr.next().find(".timesheet-entry-activity").closest("td").find(".custom-error").show();
          has_error = true;
        }
      }

      // On vérifie si on a au moins un temps
      var has_time = false;

      tr.find(".timesheet-entry-value").each(function() {
        if ($(this).val() != "") {
          has_time = true;
        }
      });

      if (!has_time) {
        tr.find(".timesheet-entry-value").addClass("has-custom-error");
        tr.next().find(".timesheet-entry-value").find(".custom-error").show();
        has_error = true;
      }
    }
  });
  
  return !has_error;    
}
</script>